import {
  defaultEditEmployeesValues,
  useEditEmployeeDrawerForm,
  EmployeeDrawerForm,
  EditEmployeeForm,
} from '@pages/employees/components';
import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerClose,
  DrawerTitle,
  Button,
  Drawer,
  toast,
} from '@ui/components';
import { EmployeeTableDataDto, EditEmployeeRequest, ResponseErrorDto } from '@interfaces/models';
import { useEmployees } from '@pages/employees/hooks/use-employees';
import { useQueryClient } from '@tanstack/react-query';
import { Cancel, Delete } from '@ui/components/icons';
import { sanitizePhoneNumber } from '@helpers/utils';
import { HasPermission } from '@shared/components';
import { useHasPermission } from '@shared/hooks';
import { useTranslation } from 'react-i18next';

interface EditEmployeeDrawerProps {
  selectedEmployee: EmployeeTableDataDto;
  isOpen: boolean;
  onOpenRemoveEmployeeDialog: (employee: EmployeeTableDataDto) => void;
  onClose: () => void;
}

export const EditEmployeeDrawer = (props: EditEmployeeDrawerProps) => {
  const { isOpen, selectedEmployee, onClose, onOpenRemoveEmployeeDialog } = props;

  const { t } = useTranslation();
  const { form } = useEditEmployeeDrawerForm();
  const queryClient = useQueryClient();

  const { mutateEditEmployee, isEditEmployeePending } = useEmployees({
    onEditSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      onClose();
      toast.success(t('EMPLOYEES.EDIT_EMPLOYEE_SUCCESS'));
    },
    onEditError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const handleSave = (employee: EditEmployeeForm) => {
    const data: EditEmployeeRequest = {
      id: selectedEmployee?.id,
      firstName: employee.firstName || null,
      lastName: employee.lastName || null,
      phoneNumber: sanitizePhoneNumber(employee.phoneNumber),
      phoneExt: employee.phoneExt,
      email: employee.email,
      labels: employee.labels,
      assignedVehicles: employee.assignedVehicles,
      profiles: employee.profiles,
      costCenter: employee.costCenter,
    };

    mutateEditEmployee({
      employee: data,
      id: selectedEmployee?.id,
    });
  };

  const hasEditPermission = useHasPermission('EMPLOYEES_EDIT_EMPLOYEE', 'WRITE');
  const isEditBlocked = !hasEditPermission;

  return (
    <Drawer
      onClose={() => {
        form.reset(defaultEditEmployeesValues);
        onClose();
      }}
      open={isOpen}
      handleOnly
    >
      <DrawerContent className="gap-0">
        <DrawerHeader className="flex">
          <DrawerTitle>
            {!selectedEmployee
              ? t('EMPLOYEES.ADD_EMPLOYEE_DRAWER_TITLE')
              : t('EMPLOYEES.EDIT_EMPLOYEE_DRAWER_TITLE')}
          </DrawerTitle>
          <div className="flex ml-auto">
            <HasPermission permissionFeature="EMPLOYEES_EDIT_EMPLOYEE" permissionType="WRITE">
              <Button
                onClick={() => onOpenRemoveEmployeeDialog(selectedEmployee)}
                variant="ghost"
                type="button"
              >
                <Delete />
              </Button>
            </HasPermission>
            <DrawerClose asChild>
              <Button variant="ghost" type="button">
                <Cancel />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="p-4 overflow-y-auto flex-1">
          <EmployeeDrawerForm
            selectedEmployee={selectedEmployee}
            isEditBlocked={isEditBlocked}
            form={form}
          />
        </div>
        <DrawerFooter>
          <Button
            disabled={isEditEmployeePending || isEditBlocked}
            onClick={form.handleSubmit(handleSave)}
            isLoading={isEditEmployeePending}
            size="large"
            wide
          >
            {t('EMPLOYEES.DRAWER_SAVE_CHANGES')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
